import { useMemo, useState } from 'react';
import { Activity, Building2, CalendarClock, Gauge, LineChart, PieChart, UserCheck } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent, type ChartConfig,
} from '@/components/ui/chart';
import type { MetricasUsuario } from '@/lib/usuarioExport';
import type { PeriodoPreset } from '@/lib/periodoFiltro';
import type { AtividadeAgregada } from '@/services/api';
import { GraficoBarrasClicavel, ListaPessoas, PessoaDaBarra, BarraClicavel } from './GraficoBarrasClicavel';
import { COR, capitalizar, haQuantoTempo, rotulosUnicos } from './graficoPadrao';

const MODULOS = [
  { key: 'okrs', label: 'OKRs' },
  { key: 'projetos', label: 'Projetos' },
  { key: 'cronogramas', label: 'Anexos em projetos' },
  { key: 'fluxogramas', label: 'Fluxogramas' },
  { key: 'organogramas', label: 'Organogramas' },
  { key: 'calendario', label: 'Calendário' },
] as const;

const TOTAL_MODULOS = MODULOS.length;

// Cores fixas por módulo na série semanal (mesma ordem de MODULOS; "Outros" = auditoria de empresa etc.)
const COR_MODULO_SEMANA: Record<string, string> = {
  OKRs: '#3b82f6',
  Projetos: '#8b5cf6',
  Cronogramas: '#06b6d4',
  Fluxogramas: '#10b981',
  Organogramas: '#f59e0b',
  Calendário: '#ec4899',
  Outros: '#94a3b8',
};

const icone = (Icon: typeof Activity) => <Icon className="h-4 w-4 text-muted-foreground" />;

const formatarSemana = (ymd: string) => {
  const [, m, d] = ymd.split('-');
  return `${d}/${m}`;
};

interface ModulosDesempenhoProps {
  metricas: MetricasUsuario[];
  periodoPreset: PeriodoPreset;
  atividade: AtividadeAgregada | null;
  mostrarEmpresas: boolean;
}

/** Aba Usuários > Módulos: gráficos por categoria, cada barra clicável mostra quem está nela. */
export function ModulosDesempenho({ metricas, periodoPreset, atividade, mostrarEmpresas }: ModulosDesempenhoProps) {
  const rotulos = useMemo(() => rotulosUnicos(metricas), [metricas]);
  const nome = (m: MetricasUsuario) => rotulos.get(m.id) ?? m.nome;
  const linkPessoa = (id: string, modulo?: string) => {
    const qs = new URLSearchParams();
    if (modulo) qs.set('modulo', modulo);
    if (periodoPreset !== 'tudo') qs.set('periodo', periodoPreset);
    const q = qs.toString();
    return `/usuarios/${id}${q ? `?${q}` : ''}`;
  };
  const porNome = (a: PessoaDaBarra, b: PessoaDaBarra) => a.nome.localeCompare(b.nome);

  // 1. Atividade total por módulo
  const barrasAtividade: BarraClicavel[] = MODULOS.map((mod) => ({
    chave: mod.key,
    rotulo: mod.label,
    valor: metricas.reduce((s, m) => s + m[mod.key], 0),
    cor: COR.destaque,
  }));
  const pessoasAtividade = (chave: string) => {
    const k = chave as (typeof MODULOS)[number]['key'];
    return metricas
      .filter((m) => m[k] > 0)
      .sort((a, b) => b[k] - a[k])
      .map((m) => ({ id: m.id, nome: nome(m), detalhe: String(m[k]) }));
  };

  // 2. Adoção por módulo: quantas pessoas usam (≥ 1) cada módulo
  const barrasAdocao: BarraClicavel[] = MODULOS.map((mod) => {
    const usam = metricas.filter((m) => m[mod.key] > 0).length;
    const fracao = metricas.length ? usam / metricas.length : 0;
    return {
      chave: mod.key,
      rotulo: `${mod.label} (${usam} de ${metricas.length})`,
      valor: usam,
      cor: fracao >= 0.5 ? COR.bom : fracao > 0 ? COR.atencao : COR.ruim,
    };
  });
  const pessoasAdocao = (chave: string) => {
    const k = chave as (typeof MODULOS)[number]['key'];
    const usam = metricas.filter((m) => m[k] > 0).sort((a, b) => b[k] - a[k])
      .map((m) => ({ id: m.id, nome: nome(m), detalhe: `usa (${m[k]})` }));
    const nunca = metricas.filter((m) => m[k] === 0)
      .map((m) => ({ id: m.id, nome: nome(m), detalhe: 'nunca usou', apagada: true }))
      .sort(porNome);
    return [...usam, ...nunca];
  };

  // 3. Nível de utilização (quantos dos 6 módulos a pessoa usa)
  const niveis = [
    { chave: 'Alto', cor: COR.bom },
    { chave: 'Médio', cor: COR.atencao },
    { chave: 'Baixo', cor: COR.ruim },
  ];
  const barrasNivel: BarraClicavel[] = niveis.map((n) => ({
    chave: n.chave,
    rotulo: n.chave,
    valor: metricas.filter((m) => m.nivelUtilizacao === n.chave).length,
    cor: n.cor,
  }));
  const pessoasNivel = (chave: string) => metricas
    .filter((m) => m.nivelUtilizacao === chave)
    .map((m) => ({ id: m.id, nome: nome(m), detalhe: `${Math.round(m.produtividade * TOTAL_MODULOS)} de ${TOTAL_MODULOS} módulos` }))
    .sort((a, b) => b.detalhe.localeCompare(a.detalhe) || porNome(a, b));

  // 4. Status de acesso
  const status = [
    { chave: 'Ativo', cor: COR.bom },
    { chave: 'Em acompanhamento', cor: COR.atencao },
    { chave: 'Inativo', cor: COR.ruim },
    { chave: 'Nunca acessou', cor: COR.neutro },
  ];
  const barrasStatus: BarraClicavel[] = status.map((st) => ({
    chave: st.chave,
    rotulo: st.chave,
    valor: metricas.filter((m) => m.statusAcessoLabel === st.chave).length,
    cor: st.cor,
  }));
  const pessoasStatus = (chave: string) => metricas
    .filter((m) => m.statusAcessoLabel === chave)
    .sort((a, b) => (a.dias ?? Infinity) - (b.dias ?? Infinity))
    .map((m) => ({ id: m.id, nome: nome(m), detalhe: haQuantoTempo(m.dias) }));

  // 5. Dias desde o último acesso
  const faixas = [
    { chave: '0-7', rotulo: '0 a 7 dias', cor: COR.bom, cabe: (d: number | null) => d !== null && d <= 7 },
    { chave: '8-30', rotulo: '8 a 30 dias', cor: COR.atencao, cabe: (d: number | null) => d !== null && d > 7 && d <= 30 },
    { chave: '31-90', rotulo: '31 a 90 dias', cor: COR.ruim, cabe: (d: number | null) => d !== null && d > 30 && d <= 90 },
    { chave: '90+', rotulo: 'Mais de 90 dias', cor: COR.critico, cabe: (d: number | null) => d !== null && d > 90 },
    { chave: 'nunca', rotulo: 'Nunca acessou', cor: COR.neutro, cabe: (d: number | null) => d === null },
  ];
  const barrasDias: BarraClicavel[] = faixas.map((f) => ({
    chave: f.chave, rotulo: f.rotulo, valor: metricas.filter((m) => f.cabe(m.dias)).length, cor: f.cor,
  }));
  const pessoasDias = (chave: string) => {
    const faixa = faixas.find((f) => f.chave === chave);
    return metricas
      .filter((m) => faixa?.cabe(m.dias))
      .sort((a, b) => (a.dias ?? Infinity) - (b.dias ?? Infinity))
      .map((m) => ({ id: m.id, nome: nome(m), detalhe: haQuantoTempo(m.dias) }));
  };

  // 7. Atividade por empresa (superadmin)
  const idsVisiveis = useMemo(() => new Set(metricas.map((m) => m.id)), [metricas]);
  const empresas = useMemo(() => {
    const mapa = new Map<string, { rotulo: string; total: number; porPessoa: Map<string, number> }>();
    for (const r of atividade?.porEmpresa ?? []) {
      if (!idsVisiveis.has(r.usuario_id)) continue;
      const chave = r.empresa_id ?? 'sem';
      const item = mapa.get(chave) ?? { rotulo: r.empresa_nome ?? 'Sem empresa', total: 0, porPessoa: new Map() };
      item.total += r.total;
      item.porPessoa.set(r.usuario_id, (item.porPessoa.get(r.usuario_id) ?? 0) + r.total);
      mapa.set(chave, item);
    }
    return [...mapa.entries()].sort((a, b) => b[1].total - a[1].total);
  }, [atividade, idsVisiveis]);
  const metricaPorId = useMemo(() => new Map(metricas.map((m) => [m.id, m])), [metricas]);
  const barrasEmpresa: BarraClicavel[] = empresas.map(([chave, e]) => ({ chave, rotulo: e.rotulo, valor: e.total, cor: COR.destaque }));
  const pessoasEmpresa = (chave: string) => {
    const e = empresas.find(([k]) => k === chave)?.[1];
    if (!e) return [];
    return [...e.porPessoa.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([id, total]) => {
        const m = metricaPorId.get(id);
        return { id, nome: m ? nome(m) : id, detalhe: `${total} ações` };
      });
  };

  return (
    <div className="space-y-4">
      <SerieSemanal atividade={atividade} idsVisiveis={idsVisiveis} metricaPorId={metricaPorId} nome={nome} linkPessoa={linkPessoa} />

      <div className="grid gap-4 lg:grid-cols-2">
        <GraficoBarrasClicavel
          titulo="Atividade total por módulo"
          icone={icone(Activity)}
          barras={barrasAtividade}
          pessoasDa={pessoasAtividade}
          linkPessoa={(id) => linkPessoa(id)}
          rotuloValor="Total"
        />
        <GraficoBarrasClicavel
          titulo="Adoção por módulo"
          icone={icone(UserCheck)}
          descricao="Pessoas que usaram cada módulo pelo menos uma vez. Clique pra ver quem usa e quem nunca usou."
          barras={barrasAdocao}
          pessoasDa={pessoasAdocao}
          linkPessoa={(id) => linkPessoa(id)}
          rotuloValor="Pessoas"
        />
        <GraficoBarrasClicavel
          titulo="Nível de utilização"
          icone={icone(Gauge)}
          descricao="Alto, médio ou baixo conforme quantos dos 6 módulos a pessoa usa. Clique pra ver quem está em cada nível."
          barras={barrasNivel}
          pessoasDa={pessoasNivel}
          linkPessoa={(id) => linkPessoa(id)}
          rotuloValor="Pessoas"
        />
        <GraficoBarrasClicavel
          titulo="Status de acesso"
          icone={icone(PieChart)}
          barras={barrasStatus}
          pessoasDa={pessoasStatus}
          linkPessoa={(id) => linkPessoa(id)}
          rotuloValor="Pessoas"
        />
        <GraficoBarrasClicavel
          titulo="Dias desde o último acesso"
          icone={icone(CalendarClock)}
          barras={barrasDias}
          pessoasDa={pessoasDias}
          linkPessoa={(id) => linkPessoa(id)}
          rotuloValor="Pessoas"
        />
        {mostrarEmpresas && (
          <GraficoBarrasClicavel
            titulo="Atividade por empresa"
            icone={icone(Building2)}
            descricao="Ações registradas em cada empresa. Clique pra ver quem fez."
            barras={barrasEmpresa}
            pessoasDa={pessoasEmpresa}
            linkPessoa={(id) => linkPessoa(id, 'linha_do_tempo')}
            rotuloValor="Ações"
          />
        )}
      </div>
    </div>
  );
}

/** Ações registradas por semana, empilhadas por módulo; clicar numa semana lista quem agiu nela. */
function SerieSemanal({
  atividade, idsVisiveis, metricaPorId, nome, linkPessoa,
}: {
  atividade: AtividadeAgregada | null;
  idsVisiveis: Set<string>;
  metricaPorId: Map<string, MetricasUsuario>;
  nome: (m: MetricasUsuario) => string;
  linkPessoa: (id: string, modulo?: string) => string;
}) {
  const [semanaSelecionada, setSemanaSelecionada] = useState<string | null>(null);

  const { dados, modulos } = useMemo(() => {
    const porSemana = new Map<string, Record<string, number>>();
    const usados = new Set<string>();
    for (const r of atividade?.porSemana ?? []) {
      if (!idsVisiveis.has(r.usuario_id)) continue;
      const modulo = r.modulo in COR_MODULO_SEMANA ? r.modulo : 'Outros';
      usados.add(modulo);
      const linha = porSemana.get(r.semana) ?? {};
      linha[modulo] = (linha[modulo] ?? 0) + r.total;
      porSemana.set(r.semana, linha);
    }
    const ordem = Object.keys(COR_MODULO_SEMANA).filter((m) => usados.has(m));
    return {
      dados: [...porSemana.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([semana, v]) => ({ semana, rotulo: formatarSemana(semana), ...v })),
      modulos: ordem,
    };
  }, [atividade, idsVisiveis]);

  const config: ChartConfig = Object.fromEntries(modulos.map((m) => [m, { label: m, color: COR_MODULO_SEMANA[m] }]));

  const pessoasDaSemana = (semana: string) => {
    const porPessoa = new Map<string, { total: number; modulos: Map<string, number> }>();
    for (const r of atividade?.porSemana ?? []) {
      if (r.semana !== semana || !idsVisiveis.has(r.usuario_id)) continue;
      const p = porPessoa.get(r.usuario_id) ?? { total: 0, modulos: new Map() };
      p.total += r.total;
      const mod = capitalizar(r.modulo);
      p.modulos.set(mod, (p.modulos.get(mod) ?? 0) + r.total);
      porPessoa.set(r.usuario_id, p);
    }
    return [...porPessoa.entries()]
      .sort((a, b) => b[1].total - a[1].total)
      .map(([id, p]) => {
        const m = metricaPorId.get(id);
        const partes = [...p.modulos.entries()].sort((a, b) => b[1] - a[1]).map(([mod, n]) => `${mod} ${n}`).join(' · ');
        return { id, nome: m ? nome(m) : id, detalhe: `${p.total} ações — ${partes}` };
      });
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-1.5 text-base font-medium">
          {icone(LineChart)}
          Atividade ao longo do tempo
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Ações registradas por semana (segunda a domingo), por módulo. Sem período escolhido, mostra as últimas 12 semanas.
          Clique numa semana pra ver quem agiu.
        </p>
      </CardHeader>
      <CardContent>
        {dados.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {atividade ? 'Nenhuma ação registrada no período.' : 'Carregando…'}
          </p>
        ) : (
          <ChartContainer config={config} className="aspect-auto h-[260px] w-full">
            <BarChart
              data={dados}
              margin={{ left: 0, right: 16 }}
              onClick={(e: { activePayload?: { payload?: { semana?: string } }[] }) => {
                const semana = e?.activePayload?.[0]?.payload?.semana;
                if (semana) setSemanaSelecionada((atual) => (atual === semana ? null : semana));
              }}
              className="cursor-pointer"
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="rotulo" tickLine={false} axisLine={false} interval={0} tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={32} />
              <ChartTooltip content={<ChartTooltipContent labelFormatter={(l) => `Semana de ${l}`} />} />
              <ChartLegend content={<ChartLegendContent />} />
              {modulos.map((m, i) => (
                <Bar key={m} dataKey={m} stackId="a" fill={COR_MODULO_SEMANA[m]} radius={i === modulos.length - 1 ? [4, 4, 0, 0] : 0} />
              ))}
            </BarChart>
          </ChartContainer>
        )}
        {semanaSelecionada && (
          <ListaPessoas
            titulo={`Semana de ${formatarSemana(semanaSelecionada)}`}
            pessoas={pessoasDaSemana(semanaSelecionada)}
            onFechar={() => setSemanaSelecionada(null)}
            linkPessoa={(id) => linkPessoa(id, 'linha_do_tempo')}
          />
        )}
      </CardContent>
    </Card>
  );
}
